import { useEffect, useState } from 'react';

import { ArrowLeftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, Card, Tag } from 'antd';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { Link } from 'react-router';

import {
  EAdmissionSurveyStatus,
  useApproveAdmissionApplicationMutation,
  type ICurrentAdmissionApplication,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { ConfirmModal } from '@/shared/components';
import { DATE_FORMATTERS, ROUTES } from '@/shared/consts';
import { formatCandidateApplicationSurveyData } from '@/shared/helpers';
import { useApiRequest, useLoader } from '@/shared/hooks';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';
import { CANDIDATE_STATUS_TO_TAG_SETTINGS } from '../../consts';
import { ApplicationDataSection } from '../_components/ApplicationDataSection/ApplicationDataSection';
import { RejectApplicationModal } from '../_components/RejectApplicationModal/RejectApplicationModal';

import styles from './CurrentApplicationPage.module.scss';

interface IProp {
  applicationData: ICurrentAdmissionApplication;
  schoolId: number;
  surveyStatus: EAdmissionSurveyStatus;
  isSurveyFetching: boolean;
}

export const CurrentApplicationPage = ({ schoolId, applicationData, surveyStatus, isSurveyFetching }: IProp) => {
  const [isApproveApplicationModalOpened, setIsApproveApplicationModalOpened] = useState(false);
  const [isRejectApplicationModalOpened, setIsRejectApplicationModalOpened] = useState(false);

  const [approveApplication, { isLoading: isApplicationApproving }] = useApproveAdmissionApplicationMutation();

  const sendApiRequest = useApiRequest();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (isSurveyFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isSurveyFetching]);

  const handleApproveApplication = () =>
    sendApiRequest({
      successMessage: 'Заявка успешно одобрена',
      apiRequestCallback: () =>
        approveApplication({
          schoolId,
          applicationId: applicationData.id,
        }),
      successCallback: () => setIsApproveApplicationModalOpened(false),
    });

  const applicationSurveyData = formatCandidateApplicationSurveyData(applicationData);

  const {
    id,
    status,
    user: { full_name: fullName },
    created_at: createdAt,
  } = applicationData;

  return (
    <div className={styles.wrapper}>
      <SelectionPanelHeader currentTab={ESelectionPanelTabs.Applications} />
      <div className={styles['header-wrapper']}>
        <Link
          to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.APPLICATIONS}`}
        >
          <Button icon={<ArrowLeftOutlined />}>Назад к списку</Button>
        </Link>
        <div className={styles['main-info-wrapper']}>
          <div className={styles['name-wrapper']}>
            <p className={styles.name}>{fullName}</p>
            <Tag
              icon={CANDIDATE_STATUS_TO_TAG_SETTINGS[status].icon}
              color={CANDIDATE_STATUS_TO_TAG_SETTINGS[status].color}
            >
              {CANDIDATE_STATUS_TO_TAG_SETTINGS[status].text}
            </Tag>
          </div>
          <p className={styles['submission-date']}>
            Заявка от{' '}
            {format(createdAt, DATE_FORMATTERS.DAY_FULL_MONTH_FULL_YEAR_SEPARATOR_HOURS_MINUTES, { locale: ru })}
          </p>
        </div>
        <div className={styles['buttons-wrapper']}>
          <Button
            icon={<CheckOutlined />}
            variant="outlined"
            color="green"
            onClick={() => setIsApproveApplicationModalOpened(true)}
            disabled={
              surveyStatus !== EAdmissionSurveyStatus.Draft &&
              surveyStatus !== EAdmissionSurveyStatus.ReadyForNotifications
            }
          >
            Одобрить
          </Button>
          <Button
            icon={<CloseOutlined />}
            variant="outlined"
            color="red"
            onClick={() => setIsRejectApplicationModalOpened(true)}
            disabled={
              surveyStatus !== EAdmissionSurveyStatus.Draft &&
              surveyStatus !== EAdmissionSurveyStatus.ReadyForNotifications
            }
          >
            Отклонить
          </Button>
        </div>
      </div>
      <Card>
        <p className={styles['card-title']}>Контактная информация</p>
        <div className={styles['contact-info-wrapper']}>
          {applicationSurveyData.slice(0, 2).map(({ id, icon, title, dataValue }) => (
            <ApplicationDataSection
              key={id}
              className={styles['contact-data-section']}
              icon={icon}
              title={title}
              dataValue={dataValue}
            />
          ))}
        </div>
      </Card>
      <Card>
        <p className={styles['card-title']}>Ответы на опрос</p>
        <div className={styles['survey-data-wrapper']}>
          {applicationSurveyData.slice(2).map(({ id, icon, title, dataValue, theme }) => (
            <ApplicationDataSection
              key={id}
              className={styles['survey-data-section']}
              icon={icon}
              title={title}
              dataValue={dataValue}
              theme={theme}
            />
          ))}
        </div>
      </Card>
      <ConfirmModal
        title="Одобрить заявку"
        description={`Вы уверены, что хотите одобрить заявку участника:\n${fullName}`}
        isModalOpen={isApproveApplicationModalOpened}
        handleCancel={() => setIsApproveApplicationModalOpened(false)}
        handleConfirm={handleApproveApplication}
        shouldDisableElement={isApplicationApproving}
      />
      <RejectApplicationModal
        isModalOpen={isRejectApplicationModalOpened}
        handleCancel={() => setIsRejectApplicationModalOpened(false)}
        candidateName={fullName}
        schoolId={schoolId}
        applicationId={id}
      />
    </div>
  );
};
